import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getCustomerSyncStatusApiV1SyncStatusGet,
  getUserConnectionsApiV1UsersMeConnectionsGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  ConnectionInfo,
  IntegrationLastSyncInfo,
  NexusSdkModelsSyncStatusResponseSyncStatusResponse as SyncStatusResponse,
} from "@/backend-sdk/client/types.gen";
import { heyApiClient } from "@/lib/hey-api-client";
import {
  setGlobalSyncDetails,
  setGlobalSyncStatus,
} from "@/lib/sync-status-bus";

interface IntegrationsData {
  connections: ConnectionInfo[];
  syncStatus: SyncStatusResponse | null;
}

interface TokenIssue {
  last_error: string | null;
}

export interface UseAccountIntegrationsDataOptions {
  refetchInterval?: number | false;
  enabled?: boolean;
  /** Quanto tempo o cache vale antes de um novo observador disparar outra busca. */
  staleTime?: number;
}

export interface UseAccountIntegrationsDataReturn {
  connections: ConnectionInfo[];
  syncStatus: SyncStatusResponse | null;
  connectionStatus: Record<"google" | "meta", "connected" | "disconnected">;
  tokenIssues: Record<"google" | "meta", TokenIssue | null>;
  syncingBySource: Record<"google" | "meta", boolean>;
  pendingJobsBySource: Record<"google" | "meta", number>;
  lastSyncByIntegration: Record<string, IntegrationLastSyncInfo> | null;
  isPending: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  dataUpdatedAt: number;
}

function getCustomerUuid(): string | null {
  return localStorage.getItem("active_customer_uuid");
}

async function fetchIntegrationsData(): Promise<IntegrationsData> {
  const customerUuid = getCustomerUuid();
  if (!customerUuid) {
    return { connections: [], syncStatus: null };
  }

  const [{ data: payload }, { data: syncStatus }] = await Promise.all([
    getUserConnectionsApiV1UsersMeConnectionsGet({
      client: heyApiClient,
      throwOnError: true,
      query: { customer_uuid: customerUuid },
    }),
    getCustomerSyncStatusApiV1SyncStatusGet({
      client: heyApiClient,
      throwOnError: true,
      query: { customer_uuid: customerUuid },
    }),
  ]);

  return {
    connections: payload.connections ?? [],
    syncStatus: syncStatus,
  };
}

export function useAccountIntegrationsData(
  options: UseAccountIntegrationsDataOptions = {},
): UseAccountIntegrationsDataReturn {
  const { refetchInterval = false, enabled = true, staleTime } = options;
  const customerUuid = getCustomerUuid();
  const queryEnabled = enabled && !!customerUuid;

  const query = useQuery<IntegrationsData, Error>({
    queryKey: ["account-integrations-data", customerUuid],
    queryFn: fetchIntegrationsData,
    refetchInterval,
    enabled: queryEnabled,
    staleTime,
  });

  const { data, error, isPending, isFetching, refetch, dataUpdatedAt } = query;
  const syncStatus = data?.syncStatus ?? null;

  useEffect(() => {
    if (syncStatus) {
      const isBusy =
        !!syncStatus.is_syncing ||
        !!syncStatus.is_backfilling ||
        !!syncStatus.is_initial_sync;
      setGlobalSyncStatus(isBusy, syncStatus.source ?? null);
      setGlobalSyncDetails(syncStatus);
    }
  }, [syncStatus]);

  const connectionStatus = useMemo((): Record<
    "google" | "meta",
    "connected" | "disconnected"
  > => {
    const status: Record<"google" | "meta", "connected" | "disconnected"> = {
      google: "disconnected",
      meta: "disconnected",
    };
    if (data?.connections) {
      data.connections.forEach((conn) => {
        const source = conn.source as "google" | "meta";
        if (source === "google" || source === "meta") {
          status[source] = conn.connected ? "connected" : "disconnected";
        }
      });
    }
    return status;
  }, [data]);

  const tokenIssues = useMemo((): Record<
    "google" | "meta",
    TokenIssue | null
  > => {
    const issues: Record<"google" | "meta", TokenIssue | null> = {
      google: null,
      meta: null,
    };
    if (data?.connections) {
      data.connections.forEach((conn) => {
        const source = conn.source as "google" | "meta";
        if (source !== "google" && source !== "meta") return;
        const invalidated = (conn.integrations ?? []).find(
          (i) => i.token_status === "invalidated",
        );
        if (invalidated) {
          issues[source] = { last_error: invalidated.last_error ?? null };
        }
      });
    }
    return issues;
  }, [data]);

  const syncingBySource = useMemo((): Record<"google" | "meta", boolean> => {
    const isBusy =
      !!syncStatus?.is_syncing ||
      !!syncStatus?.is_backfilling ||
      !!syncStatus?.is_initial_sync;
    if (!isBusy) return { google: false, meta: false };

    const source = syncStatus?.source;
    const hasGoogleJobs =
      (syncStatus?.jobs_by_source?.google?.pending ?? 0) > 0;
    const hasMetaJobs = (syncStatus?.jobs_by_source?.meta?.pending ?? 0) > 0;

    if (source === "google") return { google: true, meta: hasMetaJobs };
    if (source === "meta") return { google: hasGoogleJobs, meta: true };
    return { google: true, meta: true };
  }, [syncStatus]);

  const pendingJobsBySource = useMemo((): Record<"google" | "meta", number> => {
    const source = syncStatus?.source;
    const baseGoogle = syncStatus?.jobs_by_source?.google?.pending ?? 0;
    const baseMeta = syncStatus?.jobs_by_source?.meta?.pending ?? 0;
    const backfillPending = syncStatus?.backfill_progress?.jobs_pending ?? 0;

    if (source === "google")
      return { google: baseGoogle + backfillPending, meta: baseMeta };
    if (source === "meta")
      return { google: baseGoogle, meta: baseMeta + backfillPending };
    return {
      google: baseGoogle + backfillPending,
      meta: baseMeta + backfillPending,
    };
  }, [syncStatus]);

  const lastSyncByIntegration = useMemo(() => {
    return syncStatus?.last_sync_by_integration ?? null;
  }, [syncStatus]);

  return {
    connections: data?.connections ?? [],
    syncStatus,
    connectionStatus,
    tokenIssues,
    syncingBySource,
    pendingJobsBySource,
    lastSyncByIntegration,
    isPending,
    isFetching,
    error: error ?? null,
    refetch,
    dataUpdatedAt,
  };
}
